import InformationBanner from './InformationBanner';
import { EmploymentDateInputForm, EmploymentSalaryInputForm } from './EmploymentInputs';
import { useForm } from 'react-hook-form';
import { mdiFileDocumentEditOutline } from '@mdi/js';
import Icon from '@mdi/react';
import moment from 'moment';
import { IEmploymentInfo } from '@shared/integrations/taf-api/v2/models';
import { useGetEmploymentTypes } from '@my-pages/features/employment/hooks/queries.ts';
import { formatDate } from '@shared/utils/dateUtils';
import { isEmployed, isOnParentalLeave, isOther, isStudent, isUnemployed } from '@my-pages/features/employment/services/EmploymentService.ts';
import useEmploymentFormActions from '@my-pages/features/employment/hooks/useEmploymentFormActions.ts';
import { useGetPerson } from '@my-pages/hooks/useGetPerson.ts';
import {EmploymentUpdateStatus} from "@my-pages/features/employment/types";

interface IIncomeForm {
    employment: IEmploymentInfo;
    closeForm: () => void;
    setDisplayMessage: (value: EmploymentUpdateStatus | null) => void;
}

export const IncomeForm = ({ employment, closeForm, setDisplayMessage }: IIncomeForm) => {
    const employmentTypes = useGetEmploymentTypes();
    const person = useGetPerson();
    const { updateEmployment } = useEmploymentFormActions();
    const {
        handleSubmit,
        register,
        control,
        formState: { isValid },
    } = useForm({ mode: 'onChange' });

    const submit = async (submitData: any) => {
        const submitDataWithDefaultData = {
            ...submitData,
            startDateNewIncome: formatDate(submitData.startDateNewIncome),
            description: ChangeIncomeDescription(submitData, employment),
            salary: Number(submitData.salary),
            // @ts-ignore
            employementStatusId: employment ? employmentTypes[employment.occupationalStatusName] : null,
            employmentId: employment?.employmentId,
            memberId: person.id,
            workplaceId: employment?.workplaceId,
        };

        const result = await updateEmployment(submitDataWithDefaultData);
        setDisplayMessage(result);
        closeForm();
    };

    const nextMonth = moment().add(1, 'months').startOf('month').toDate();

    return (
        <form onSubmit={handleSubmit(submit)}>
            <h3 className='d-flex'>
                <Icon
                    path={mdiFileDocumentEditOutline}
                    className='mdi mdi-36 mr-2 mt-n2'
                />
                <span>Ändra inkomst</span>
            </h3>
            <p className='required-info'>* Obligatoriska uppgifter</p>
            <EmploymentSalaryInputForm
                name={'salary'}
                defaultValue={0}
                register={{ ...register('salary', { required: true }) }}
            />
            <h4 className='mt-5'>Från vilken månad gäller dessa uppgifter?</h4>
            <EmploymentDateInputForm
                control={control}
                rules={{ required: true }}
                minDate={nextMonth}
                name={'startDateNewIncome'}
            />
            <InformationBanner />
            <div className='d-flex justify-content-sm-end mt-5 mb-4'>
                <button
                    className='btn btn-light btn-lg mr-3'
                    type='button'
                    onClick={() => closeForm()}
                >
                    Avbryt
                </button>
                <button
                    className={`btn btn-primary btn-lg`}
                    disabled={!isValid}
                >
                    Spara
                </button>
            </div>
        </form>
    );
};

const ChangeIncomeDescription = (submitData: any, employment: IEmploymentInfo) => {
    if (isEmployed(employment)) {
        return 'Medlem har anmält ändrad inkomst till ' + submitData.salary + ' kr. Tidigare var den ' + (employment?.salary ?? 0);
    } else if (isUnemployed(employment)) {
        return 'Medlem har anmält ändrad arbetslöshetsersättning till ' + submitData.salary + ' kr. Tidigare var den ' + (employment?.salary ?? 0);
    } else if (isStudent(employment)) {
        return 'Medlem har anmält ändrad studentinkomst till ' + submitData.salary + ' kr. Tidigare var den ' + (employment?.salary ?? 0);
    } else if (isOnParentalLeave(employment)) {
        return 'Medlem har anmält ändrad ersättning som föräldraledig till ' + submitData.salary + ' kr. Tidigare var den ' + (employment?.salary ?? 0);
    } else if (isOther(employment)) {
        return 'Medlem har anmält ändrad ersättning som föräldraledig till ' + submitData.salary + ' kr. Tidigare var den ' + (employment?.salary ?? 0);
    }
    return '';
};
